import React, { useState } from 'react';
import { toast } from '@/components/ui/helpers/toast';
import { useShallow } from 'zustand/react/shallow';

import * as Logger from '@/lib/monitoring/logger';

import { trackSuccessfulLogin } from '@/lib/analytics/track-successful-login';
import { useLogin } from '@/features/auth/hooks/use-login';
import { getApiErrorMessage } from '@/lib/helpers/api-errors/get-api-error-message';
import { emailRegex } from '@/lib/helpers/validation/validation';
import { useAccountActions } from '@/contexts/account/account-context';
import { useAuth } from '@/contexts/auth/auth-context';
import { useIntercom } from '@/contexts/intercom/intercom-context';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import useStore from '@/store/app/use-store';

import { usePostLoginRouter } from './use-post-login-router';

interface UseLoginFlowReturn {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  isLoading: boolean;
  isCheckingSubscription: boolean;
  login: (e?: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
}

export function useLoginFlow(): UseLoginFlowReturn {
  const { actions: { setAuthenticatedSession } } = useAuth();
  const setUser = useStore(state => state.setUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const { mutateAsync: loginMutation, isLoading } = useLogin();
  const { boot } = useIntercom();
  const { setAccountsList, setCurrentAccount } = useAccountActions();
  const { planId, reset: resetSignupFlow } = useSignupFlowStore(
    useShallow(s => ({
      planId: s.planId,
      reset: s.reset,
    }))
  );
  const { routeAfterLogin } = usePostLoginRouter(
    planId, resetSignupFlow, setIsCheckingSubscription, setCurrentAccount
  );

  const login = async (_e?: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !emailRegex.test(trimmedEmail)) {
      toast.error('invalid credentials');
      return;
    }

    try {
      const authData = await loginMutation({ email: trimmedEmail, password });
      if (!authData?.signInUserSession) {
        Logger.error('Login succeeded but no session in response');
        toast.error('Authentication error. Please try again.');
        return;
      }

      const jwtToken = authData.signInUserSession.idToken?.jwtToken;
      if (!jwtToken) {
        Logger.error('Login succeeded but no JWT token in response');
        toast.error('Authentication error. Please try again.');
        return;
      }

      setAuthenticatedSession(jwtToken);
      setUser(authData.attributes ?? null);
      Logger.setUser({
        id: authData.attributes?.sub || '',
        email: authData.attributes?.email,
        name: authData.attributes?.name,
      });

      toast.success('Login successfully');
      const userId = authData.attributes?.sub || '';
      trackSuccessfulLogin({ email: trimmedEmail, userId, flow: 'standard', method: 'email' });
      boot();
      setEmail('');
      setPassword('');
      setShowPassword(false);

      setIsCheckingSubscription(true);
      try {
        const accountsResponse = await AccountsApiService.getUserAccounts();
        const accounts = accountsResponse.data.accounts;
        if (accounts?.length) setAccountsList(accounts);
        routeAfterLogin(accounts ?? [], userId, trimmedEmail);
      } catch (err: unknown) {
        setIsCheckingSubscription(false);
        Logger.error('[Login] Failed to fetch user accounts', err instanceof Error ? err : null);
        toast.error('Failed to load account data. Please try refreshing the page.');
      }
    } catch (error: unknown) {
      Logger.error('Failed login', error instanceof Error ? error : null);
      toast.error(getApiErrorMessage(error));
    }
  };

  return {
    email, setEmail,
    password, setPassword,
    showPassword, setShowPassword,
    isLoading, isCheckingSubscription,
    login,
  };
}
